import React, { useEffect } from 'react'
import { Row, Col, Button, Label } from 'reactstrap'
import { useDispatch } from 'react-redux'

// internal imports
import { clearNetSuiteAssets } from 'store/netSuiteAssetManager/operations'
import ErrorBoundary from 'components/ErrorBoundary'
import SelectLookup from 'components/SelectLookup'
import { NetSuiteAssetFilters } from 'types'
import { clearUsers } from 'store/css-admin/userManager/actions'
import { formatUserSuggestion } from 'lib/util'

// TypeScript interfaces
interface SearchFormProps {
  clearSearch: () => void
  criteria: NetSuiteAssetFilters
  search: () => void
  handleNetSuiteAssets: (query: NetSuiteAssetFilters) => void
  updateCriteria: (key: string, value: string) => void
}

type SelectLookupSelection = {
  value: string
  label: string
}

const SearchForm: React.FC<SearchFormProps> = ({
  clearSearch,
  criteria,
  handleNetSuiteAssets,
  search,
  updateCriteria,
}) => {
  const dispatch = useDispatch()

  // Action creators
  const clearLicenses = () => dispatch(clearNetSuiteAssets())
  const clearUsersAction = () => dispatch(clearUsers())

  useEffect(() => {
    clearLicenses()
    clearUsersAction()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateCriteria(e.target.name, e.target.value)
  }

  const handleSelectChange = (
    name: string,
    selection?: SelectLookupSelection,
  ) => {
    if (selection != null && handleNetSuiteAssets) {
      handleNetSuiteAssets({
        [name]: selection.value,
      })
    }
  }

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      search()
    }
  }

  return (
    <ErrorBoundary>
      <Row>
        <Col md={12} sm={12}>
          <fieldset>
            <div
              className="form-group"
              data-testid="setupAdminEmail-search-form"
            >
              <Label>Setup Admin (Email)</Label>
              <SelectLookup
                apiVersion={2}
                formatSuggestion={formatUserSuggestion}
                onChange={selection =>
                  handleSelectChange('setupAdminEmail', selection)
                }
                placeholder={'Setup Admin Email'}
                searchEndpoint="profile/search"
                searchField="email"
                value={null} /* Value left null on purpose because 
                we always ignore the email field when searching
                with other fields */
              />
            </div>
            <div
              className="form-group"
              data-testid="contractLineNSId-search-form"
            >
              <Label>NS Contract Line Id</Label>
              <input
                className="form-control"
                name="contractLineNSId"
                onChange={handleChange}
                onKeyDown={handleKeydown}
                type="number"
                value={criteria.contractLineNSId || ''}
              />
            </div>
            <div className="form-group" data-testid="nsName-search-form">
              <Label>NS License Id</Label>
              <input
                className="form-control"
                name="nsName"
                onChange={handleChange}
                onKeyDown={handleKeydown}
                type="text"
                value={criteria.nsName || ''}
              />
            </div>
            <div className="form-group" data-testid="id-search-form">
              <Label>Internal Asset Id</Label>
              <input
                className="form-control"
                name="id"
                onChange={handleChange}
                onKeyDown={handleKeydown}
                type="number"
                value={criteria.id || ''}
              />
            </div>
          </fieldset>
        </Col>
      </Row>

      <hr />
      <div className="text-right">
        <Button
          className="btn-fixed-width"
          color="link"
          data-testid="clear-button"
          onClick={clearSearch}
        >
          Clear
        </Button>
        <Button
          className="btn-fixed-width"
          color="primary"
          data-testid="search-button"
          disabled={Object.keys(criteria).length === 0}
          onClick={search}
        >
          <i className="fas fa-search" />
          &nbsp;Search
        </Button>
      </div>
    </ErrorBoundary>
  )
}

export default SearchForm
